import React from "react";
import { EOS_REPORT_TIME_DIVISOR } from "../../../../constants/customers";
import { PnlCategoriesEnum, ReportParams } from "../../../../types";
import {
  getFormattedMinutes,
  getReadableHoursAndMinutes,
  ONE_MINUTE_OF_MS,
} from "reports/utils/timeUtils";
import CollapsibleSubRows from "../CollapsibleSubRows";
import PercentBar from "./PercentBar";
import { PnLChartStyles } from "./pnlChartStyles";
import { filterByPercentValue } from "./pnlChartUtils";
import CardWrapperStyles from "../../CardWrapperStyles";
import { useTranslation } from "react-i18next";
import { PnlInstanceTypeWithTitle, PnlTimes } from "./pnlChartTypes";
import { useParams } from "react-router";
import { camelCase } from "lodash";

type PnlRowType = PnlInstanceTypeWithTitle & {
  timeDivisor?: number;
  shouldShowTimes?: boolean;
  shouldShowAvgDuration?: boolean;
  shouldShowNumDtSegments?: boolean;
  shouldShowLostOutput?: boolean;
  shouldShowPercent?: boolean;
  isSubTag?: boolean;
};

type PnlChartProps = {
  pnlTimes: PnlTimes[];
  shouldShowTimes?: boolean;
  shouldShowAvgDuration?: boolean;
  shouldShowNumDtSegments?: boolean;
  shouldShowLostOutput?: boolean;
  shouldShowPercent?: boolean;
  customHeader?: string;
  lostOutputColumnHeader?: string;
  /** don't auto-collapse tags under 1 percent of total time */
  shouldUncollapseAllTags?: boolean;
};

const PnLRow = ({
  title,
  value,
  weightedValue,
  timeDivisor,
  isSubTag = false,
  color,
  time = 0,
  nbEvents = 0,
  avgDuration = 0,
  lostOutput = 0,
  shouldFillWithLines = false,
  shouldShowTimes,
  shouldHideValue = false,
  shouldShowAvgDuration,
  shouldShowNumDtSegments,
  shouldShowLostOutput,
  shouldShowPercent = true,
  hidePercentBar = false,
  hideBorder = false,
}: PnlRowType) => {
  const { t } = useTranslation("EOSReport");
  const timeInMinutes = getFormattedMinutes(time);

  const readableTime =
    timeDivisor === ONE_MINUTE_OF_MS
      ? `${timeInMinutes} min`
      : getReadableHoursAndMinutes(time);
  const readableAvgDuration = getReadableHoursAndMinutes(avgDuration);

  const isTimeZero = time === 0;

  return isSubTag && isTimeZero ? null : (
    <>
      <div className="title">
        {isSubTag ? null : (
          <div className="circleDiv">
            <span className="circle" style={{ backgroundColor: color }} />
          </div>
        )}
        {isSubTag ? title : t(`${title}`)}
      </div>
      {shouldShowNumDtSegments && <div className="nbEvents">{nbEvents}</div>}
      {shouldShowAvgDuration && (
        <div className="avgDuration">{readableAvgDuration}</div>
      )}
      {shouldShowLostOutput && <div className="lostOutput">{lostOutput}</div>}
      {shouldShowTimes && (
        <div className="time time-data">
          <div>{readableTime}</div>
        </div>
      )}
      {shouldShowPercent &&
        (!shouldHideValue ? (
          <div className="percent-value time">{`${
            value ? value + "%" : "--"
          }`}</div>
        ) : (
          <div className="percent-value" />
        ))}
      <div className="bar">
        {!hidePercentBar && (
          <PercentBar
            percent={weightedValue || value || 0}
            color={color}
            shouldFillWithLines={shouldFillWithLines}
            hideBorder={hideBorder}
          />
        )}
      </div>
    </>
  );
};

// tslint:disable-next-line: cognitive-complexity
export default function PnlChart({
  pnlTimes,
  shouldShowTimes = true,
  shouldShowNumDtSegments = true,
  shouldShowAvgDuration = false,
  shouldShowLostOutput = false,
  lostOutputColumnHeader = "Lost Output",
  shouldShowPercent = true,
  customHeader,
  shouldUncollapseAllTags,
}: PnlChartProps) {
  const { t } = useTranslation("EOSReport");
  const { ckey } = useParams() as ReportParams;
  const timeDivisor = EOS_REPORT_TIME_DIVISOR?.[ckey];

  const numColumns = [
    shouldShowTimes,
    shouldShowNumDtSegments,
    shouldShowAvgDuration,
    shouldShowLostOutput,
    shouldShowPercent,
  ].filter(col => col).length;

  const maxCategoryOrTagNameLength = Math.max(
    ...pnlTimes.map(pnlTime => {
      const tagNameLengths = pnlTime?.subTags
        ? pnlTime.subTags.map(tag => tag?.title.length)
        : [];
      return tagNameLengths && tagNameLengths.length > 0
        ? Math.max(...tagNameLengths)
        : pnlTime.name.length;
    })
  );
  const shouldUncollapseAllTagsFromUrl = window.location.search.includes(
    "disableShowMore=true"
  )
    ? true
    : undefined;

  return (
    <CardWrapperStyles>
      <PnLChartStyles
        {...{
          $maxCategoryOrTagNameLength: maxCategoryOrTagNameLength,
          $numColumns: numColumns,
        }}
      >
        <div className="header">
          {`${t("pnlHeader")}${customHeader ? customHeader : ""}`}
        </div>
        <div className="subHeader">{t("pnlSubheader")}</div>
        <div className="row pnlCategory">
          <div className="title" />
          {shouldShowNumDtSegments && (
            <div className="nbEvents">{t("numEvents")}</div>
          )}
          {shouldShowAvgDuration && (
            <div className="avgDuration">{t("avgDuration")}</div>
          )}
          {shouldShowLostOutput && (
            <div className="lostOutput">{lostOutputColumnHeader}</div>
          )}
          {shouldShowTimes && <div className="time">{t("time")}</div>}
          {shouldShowPercent && <div className="percent">%</div>}
          <div className="bar" />
        </div>

        {pnlTimes.map(pnlTime => {
          const subTagsOver1Percent = filterByPercentValue({
            pnlSubTagArray: pnlTime.subTags,
            shouldBeLessThan: false,
          });
          const subTagsUnder1Percent = filterByPercentValue({
            pnlSubTagArray: pnlTime.subTags,
            shouldBeLessThan: true,
          });

          const subTags = subTagsList =>
            subTagsList?.map(subTag => {
              return (
                <div
                  className={`row subTagRow subTagRow-${camelCase(
                    subTag?.name
                  )}`}
                  key={subTag?.name}
                >
                  <PnLRow
                    isSubTag={true}
                    {...subTag}
                    {...{
                      timeDivisor,
                      shouldShowTimes,
                      shouldShowNumDtSegments,
                      shouldShowLostOutput,
                      shouldShowPercent,
                      shouldShowAvgDuration,
                    }}
                  />
                </div>
              );
            });

          const numberOfSubTags =
            subTagsOver1Percent && subTagsUnder1Percent
              ? subTagsOver1Percent.length + subTagsUnder1Percent.length
              : subTagsOver1Percent
                ? subTagsOver1Percent.length
                : subTagsUnder1Percent
                  ? subTagsUnder1Percent.length
                  : 0;

          return (
            <React.Fragment key={pnlTime.name}>
              <div className="row pnlCategory">
                <PnLRow
                  {...pnlTime}
                  {...{
                    timeDivisor,
                    shouldShowTimes,
                    shouldShowNumDtSegments,
                    shouldShowLostOutput,
                    shouldShowPercent,
                    shouldShowAvgDuration,
                  }}
                />
              </div>
              {pnlTime.name.toLowerCase() === PnlCategoriesEnum.producing ||
              (shouldUncollapseAllTagsFromUrl ?? shouldUncollapseAllTags) ? (
                subTags(pnlTime?.subTags)
              ) : numberOfSubTags <= 5 ? (
                <>
                  {subTagsOver1Percent && subTags(subTagsOver1Percent)}
                  {subTagsUnder1Percent && subTags(subTagsUnder1Percent)}
                </>
              ) : (
                <>
                  {subTagsOver1Percent && subTags(subTagsOver1Percent)}
                  {subTagsUnder1Percent && subTagsUnder1Percent.length > 0 && (
                    <CollapsibleSubRows
                      subRows={subTags(subTagsUnder1Percent)}
                      showMoreTitle={`See more <1%`}
                      showLessTitle={`See less <1%`}
                    />
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </PnLChartStyles>
    </CardWrapperStyles>
  );
}
